// Consent (GDPR) for the viewer's analytics, read from ConsentManager.
//
// The CMP's consent scope is domain-specific (*.airteam.cloud), so a choice
// made on airteam.cloud already applies here and the banner only shows for
// visitors who have not chosen anywhere yet (e.g. opening a shared link).
// Inside an iframe index.html does not load the CMP. There, the only consent
// source is Bobbie: when bobbie.de embeds us (it navigates its iframe from
// airteam.cloud to /bobbie) it pushes its banner's choice down via
// postMessage, as it does for the AFP frontend (bobbie-iframe-sync.js there).
// Control Center is internal and never gets consent, so analytics stay off.

// Vendor ids in the ConsentManager dashboard.
export const CMP_VENDORS = {
  posthog: "c89783",
  googleAnalytics: "s26",
};

const listeners = [];

// listener({ posthog: boolean, googleAnalytics: boolean })
export function onConsentChange(listener) {
  listeners.push(listener);
}

function readVendorConsents() {
  try {
    return window.__cmp("getCMPData")?.vendorConsents;
  } catch (e) {
    return undefined;
  }
}

export function initCmpConsent() {
  // No CMP on the page (iframe context, blocked script): stay opted out.
  if (typeof window.__cmp !== "function") return;

  const sync = () => {
    const vendorConsents = readVendorConsents();
    if (!vendorConsents) return;
    const consent = {
      posthog: Boolean(vendorConsents[CMP_VENDORS.posthog]),
      googleAnalytics: Boolean(vendorConsents[CMP_VENDORS.googleAnalytics]),
    };
    listeners.forEach((listener) => listener(consent));
  };

  // Fires when the user saves a choice in the banner, and on load once the CMP
  // has read a choice stored earlier (here or on airteam.cloud).
  window.__cmp("addEventListener", ["consent", sync, false], null);
  // If the CMP is already loaded, honor the stored choice right away.
  sync();
}

// Bobbie's banner has not yet confirmed that its analytics consent covers
// PostHog. Until it does, Bobbie's consent only drives Google Analytics and
// PostHog stays off inside the Bobbie iframe. Set to true once Bobbie confirms
// (banner/privacy policy name Airteam's analytics). Same switch as in AFP.
const BOBBIE_CONSENT_COVERS_POSTHOG = false;

// Origins of the Bobbie host page we accept consent from (same as AFP).
const BOBBIE_ORIGINS = ["https://www.bobbie.de", "https://cs-test.bobbie.de"];

function isGranted(value) {
  return (
    value === true ||
    value === 1 ||
    value === "true" ||
    value === "granted" ||
    value === "yes"
  );
}

// Bobbie sends HubSpot-style consent whose exact shape is not pinned: accept a
// plain boolean, a flat category object, or one nested under `categories`.
function isAnalyticsGranted(consent) {
  if (typeof consent === "boolean") return consent;
  const c = consent || {};
  const categories = c.categories || c;
  return isGranted(categories.analytics) || isGranted(c.allowed);
}

export function initBobbieConsent() {
  if (window.self === window.top) return;

  window.addEventListener("message", (event) => {
    if (!BOBBIE_ORIGINS.includes(event.origin)) return;
    if (event.data?.type !== "COOKIE_CONSENT") return;
    const analytics = isAnalyticsGranted(event.data.consent);
    listeners.forEach((listener) =>
      listener({
        posthog: BOBBIE_CONSENT_COVERS_POSTHOG && analytics,
        googleAnalytics: analytics,
      }),
    );
  });

  // Ask the host page for the current choice. Only Bobbie answers; any other
  // embedder (Control Center) ignores it.
  try {
    window.parent.postMessage({ type: "COOKIE_CONSENT_REQUEST" }, "*");
  } catch (e) {
    /* parent unreachable — stay opted out */
  }
}
